/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * API specification
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  AdminProfile,
  AuthCheckResponse,
  BadRequestResponse,
  ConflictResponse,
  CreateEngineerRequest,
  CreateJobRequest,
  CreateSiteRequest,
  Engineer,
  ForbiddenResponse,
  HealthStatus,
  Job,
  LoginRequest,
  LoginResponse,
  NotFoundResponse,
  PpmChecklistPhoto,
  PpmChecklistPhotoUploadRequest,
  PpmChecklistPhotoUploadResponse,
  PpmInspection,
  PpmInspectionInput,
  PpmInspectionPatchInput,
  Site,
  SiteCoverUploadRequest,
  SiteCoverUploadResponse,
  SuccessResponse,
  UnauthorizedResponse,
  UpdateAdminJobRequest,
  UpdateEngineerRequest,
  UpdateJobReportRequest,
  UpdateSiteRequest,
} from "./api.schemas";

import { customFetch } from "../custom-fetch";
import type { ErrorType, BodyType } from "../custom-fetch";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Returns server health status
 * @summary Health check
 */
export const getHealthCheckUrl = () => {
  return `/api/healthz`;
};

export const healthCheck = async (
  options?: RequestInit,
): Promise<HealthStatus> => {
  return customFetch<HealthStatus>(getHealthCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getHealthCheckQueryKey = () => {
  return [`/api/healthz`] as const;
};

export const getHealthCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthCheck>>> = ({
    signal,
  }) => healthCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type HealthCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthCheck>>
>;
export type HealthCheckQueryError = ErrorType<unknown>;

/**
 * @summary Health check
 */

export function useHealthCheck<
  TData = Awaited<ReturnType<typeof healthCheck>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof healthCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getHealthCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Sign in an admin or engineer
 */
export const getAdminLoginUrl = () => {
  return `/api/admin/login`;
};

export const adminLogin = async (
  loginRequest: LoginRequest,
  options?: RequestInit,
): Promise<LoginResponse> => {
  return customFetch<LoginResponse>(getAdminLoginUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(loginRequest),
  });
};

export const getAdminLoginMutationOptions = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminLogin>>,
    TError,
    { data: BodyType<LoginRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminLogin>>,
  TError,
  { data: BodyType<LoginRequest> },
  TContext
> => {
  const mutationKey = ["adminLogin"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminLogin>>,
    { data: BodyType<LoginRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return adminLogin(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminLogin>>
>;
export type AdminLoginMutationBody = BodyType<LoginRequest>;
export type AdminLoginMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse
>;

/**
 * @summary Sign in an admin or engineer
 */
export const useAdminLogin = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminLogin>>,
    TError,
    { data: BodyType<LoginRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminLogin>>,
  TError,
  { data: BodyType<LoginRequest> },
  TContext
> => {
  return useMutation(getAdminLoginMutationOptions(options));
};

/**
 * @summary Sign out
 */
export const getAdminLogoutUrl = () => {
  return `/api/admin/logout`;
};

export const adminLogout = async (
  options?: RequestInit,
): Promise<SuccessResponse> => {
  return customFetch<SuccessResponse>(getAdminLogoutUrl(), {
    ...options,
    method: "POST",
  });
};

export const getAdminLogoutMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminLogout>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminLogout>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ["adminLogout"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminLogout>>,
    void
  > = () => {
    return adminLogout(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminLogoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminLogout>>
>;

export type AdminLogoutMutationError = ErrorType<unknown>;

/**
 * @summary Sign out
 */
export const useAdminLogout = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminLogout>>,
    TError,
    void,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof adminLogout>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getAdminLogoutMutationOptions(options));
};

/**
 * @summary Check the current admin or engineer session
 */
export const getAdminCheckUrl = () => {
  return `/api/admin/check`;
};

export const adminCheck = async (
  options?: RequestInit,
): Promise<AuthCheckResponse> => {
  return customFetch<AuthCheckResponse>(getAdminCheckUrl(), {
    ...options,
    method: "GET",
  });
};

export const getAdminCheckQueryKey = () => {
  return [`/api/admin/check`] as const;
};

export const getAdminCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof adminCheck>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAdminCheckQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof adminCheck>>> = ({
    signal,
  }) => adminCheck({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof adminCheck>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type AdminCheckQueryResult = NonNullable<
  Awaited<ReturnType<typeof adminCheck>>
>;
export type AdminCheckQueryError = ErrorType<UnauthorizedResponse>;

/**
 * @summary Check the current admin or engineer session
 */

export function useAdminCheck<
  TData = Awaited<ReturnType<typeof adminCheck>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof adminCheck>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getAdminCheckQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get the current admin or engineer profile
 */
export const getGetAdminMeUrl = () => {
  return `/api/admin/me`;
};

export const getAdminMe = async (
  options?: RequestInit,
): Promise<AdminProfile> => {
  return customFetch<AdminProfile>(getGetAdminMeUrl(), {
    ...options,
    method: "GET",
  });
};

export const getGetAdminMeQueryKey = () => {
  return [`/api/admin/me`] as const;
};

export const getGetAdminMeQueryOptions = <
  TData = Awaited<ReturnType<typeof getAdminMe>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAdminMe>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAdminMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminMe>>> = ({
    signal,
  }) => getAdminMe({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAdminMe>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetAdminMeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAdminMe>>
>;
export type GetAdminMeQueryError = ErrorType<UnauthorizedResponse>;

/**
 * @summary Get the current admin or engineer profile
 */

export function useGetAdminMe<
  TData = Awaited<ReturnType<typeof getAdminMe>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAdminMe>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetAdminMeQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary List engineer accounts
 */
export const getListEngineersUrl = () => {
  return `/api/admin/engineers`;
};

export const listEngineers = async (
  options?: RequestInit,
): Promise<Engineer[]> => {
  return customFetch<Engineer[]>(getListEngineersUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListEngineersQueryKey = () => {
  return [`/api/admin/engineers`] as const;
};

export const getListEngineersQueryOptions = <
  TData = Awaited<ReturnType<typeof listEngineers>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listEngineers>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListEngineersQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listEngineers>>> = ({
    signal,
  }) => listEngineers({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listEngineers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListEngineersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listEngineers>>
>;
export type ListEngineersQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary List engineer accounts
 */

export function useListEngineers<
  TData = Awaited<ReturnType<typeof listEngineers>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listEngineers>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListEngineersQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create an engineer account
 */
export const getCreateEngineerUrl = () => {
  return `/api/admin/engineers`;
};

export const createEngineer = async (
  createEngineerRequest: CreateEngineerRequest,
  options?: RequestInit,
): Promise<Engineer> => {
  return customFetch<Engineer>(getCreateEngineerUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createEngineerRequest),
  });
};

export const getCreateEngineerMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createEngineer>>,
    TError,
    { data: BodyType<CreateEngineerRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createEngineer>>,
  TError,
  { data: BodyType<CreateEngineerRequest> },
  TContext
> => {
  const mutationKey = ["createEngineer"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createEngineer>>,
    { data: BodyType<CreateEngineerRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createEngineer(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateEngineerMutationResult = NonNullable<
  Awaited<ReturnType<typeof createEngineer>>
>;
export type CreateEngineerMutationBody = BodyType<CreateEngineerRequest>;
export type CreateEngineerMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | ConflictResponse
>;

/**
 * @summary Create an engineer account
 */
export const useCreateEngineer = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createEngineer>>,
    TError,
    { data: BodyType<CreateEngineerRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createEngineer>>,
  TError,
  { data: BodyType<CreateEngineerRequest> },
  TContext
> => {
  return useMutation(getCreateEngineerMutationOptions(options));
};

/**
 * @summary Update an engineer account
 */
export const getUpdateEngineerUrl = (id: number) => {
  return `/api/admin/engineers/${id}`;
};

export const updateEngineer = async (
  id: number,
  updateEngineerRequest: UpdateEngineerRequest,
  options?: RequestInit,
): Promise<Engineer> => {
  return customFetch<Engineer>(getUpdateEngineerUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateEngineerRequest),
  });
};

export const getUpdateEngineerMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateEngineer>>,
    TError,
    { id: number; data: BodyType<UpdateEngineerRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateEngineer>>,
  TError,
  { id: number; data: BodyType<UpdateEngineerRequest> },
  TContext
> => {
  const mutationKey = ["updateEngineer"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateEngineer>>,
    { id: number; data: BodyType<UpdateEngineerRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateEngineer(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateEngineerMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateEngineer>>
>;
export type UpdateEngineerMutationBody = BodyType<UpdateEngineerRequest>;
export type UpdateEngineerMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Update an engineer account
 */
export const useUpdateEngineer = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateEngineer>>,
    TError,
    { id: number; data: BodyType<UpdateEngineerRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateEngineer>>,
  TError,
  { id: number; data: BodyType<UpdateEngineerRequest> },
  TContext
> => {
  return useMutation(getUpdateEngineerMutationOptions(options));
};

/**
 * @summary List all sites, including inactive sites
 */
export const getListAdminSitesUrl = () => {
  return `/api/admin/sites`;
};

export const listAdminSites = async (
  options?: RequestInit,
): Promise<Site[]> => {
  return customFetch<Site[]>(getListAdminSitesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListAdminSitesQueryKey = () => {
  return [`/api/admin/sites`] as const;
};

export const getListAdminSitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listAdminSites>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAdminSites>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAdminSitesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminSites>>> = ({
    signal,
  }) => listAdminSites({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAdminSites>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAdminSitesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAdminSites>>
>;
export type ListAdminSitesQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary List all sites, including inactive sites
 */

export function useListAdminSites<
  TData = Awaited<ReturnType<typeof listAdminSites>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAdminSites>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAdminSitesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a site
 */
export const getCreateAdminSiteUrl = () => {
  return `/api/admin/sites`;
};

export const createAdminSite = async (
  createSiteRequest: CreateSiteRequest,
  options?: RequestInit,
): Promise<Site> => {
  return customFetch<Site>(getCreateAdminSiteUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createSiteRequest),
  });
};

export const getCreateAdminSiteMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAdminSite>>,
    TError,
    { data: BodyType<CreateSiteRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAdminSite>>,
  TError,
  { data: BodyType<CreateSiteRequest> },
  TContext
> => {
  const mutationKey = ["createAdminSite"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAdminSite>>,
    { data: BodyType<CreateSiteRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createAdminSite(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAdminSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAdminSite>>
>;
export type CreateAdminSiteMutationBody = BodyType<CreateSiteRequest>;
export type CreateAdminSiteMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Create a site
 */
export const useCreateAdminSite = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAdminSite>>,
    TError,
    { data: BodyType<CreateSiteRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createAdminSite>>,
  TError,
  { data: BodyType<CreateSiteRequest> },
  TContext
> => {
  return useMutation(getCreateAdminSiteMutationOptions(options));
};

/**
 * @summary Update a site
 */
export const getUpdateAdminSiteUrl = (id: number) => {
  return `/api/admin/sites/${id}`;
};

export const updateAdminSite = async (
  id: number,
  updateSiteRequest: UpdateSiteRequest,
  options?: RequestInit,
): Promise<Site> => {
  return customFetch<Site>(getUpdateAdminSiteUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateSiteRequest),
  });
};

export const getUpdateAdminSiteMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAdminSite>>,
    TError,
    { id: number; data: BodyType<UpdateSiteRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAdminSite>>,
  TError,
  { id: number; data: BodyType<UpdateSiteRequest> },
  TContext
> => {
  const mutationKey = ["updateAdminSite"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAdminSite>>,
    { id: number; data: BodyType<UpdateSiteRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAdminSite(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAdminSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAdminSite>>
>;
export type UpdateAdminSiteMutationBody = BodyType<UpdateSiteRequest>;
export type UpdateAdminSiteMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update a site
 */
export const useUpdateAdminSite = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAdminSite>>,
    TError,
    { id: number; data: BodyType<UpdateSiteRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateAdminSite>>,
  TError,
  { id: number; data: BodyType<UpdateSiteRequest> },
  TContext
> => {
  return useMutation(getUpdateAdminSiteMutationOptions(options));
};

/**
 * @summary Archive a site without deleting its historical records
 */
export const getArchiveAdminSiteUrl = (id: number) => {
  return `/api/admin/sites/${id}`;
};

export const archiveAdminSite = async (
  id: number,
  options?: RequestInit,
): Promise<Site> => {
  return customFetch<Site>(getArchiveAdminSiteUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getArchiveAdminSiteMutationOptions = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveAdminSite>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof archiveAdminSite>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["archiveAdminSite"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof archiveAdminSite>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return archiveAdminSite(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ArchiveAdminSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof archiveAdminSite>>
>;

export type ArchiveAdminSiteMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Archive a site without deleting its historical records
 */
export const useArchiveAdminSite = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveAdminSite>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof archiveAdminSite>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getArchiveAdminSiteMutationOptions(options));
};

/**
 * @summary Request a direct-upload URL for a site cover image
 */
export const getRequestSiteCoverUploadUrl = () => {
  return `/api/admin/sites/cover-upload`;
};

export const requestSiteCoverUpload = async (
  siteCoverUploadRequest: SiteCoverUploadRequest,
  options?: RequestInit,
): Promise<SiteCoverUploadResponse> => {
  return customFetch<SiteCoverUploadResponse>(getRequestSiteCoverUploadUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(siteCoverUploadRequest),
  });
};

export const getRequestSiteCoverUploadMutationOptions = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestSiteCoverUpload>>,
    TError,
    { data: BodyType<SiteCoverUploadRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof requestSiteCoverUpload>>,
  TError,
  { data: BodyType<SiteCoverUploadRequest> },
  TContext
> => {
  const mutationKey = ["requestSiteCoverUpload"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof requestSiteCoverUpload>>,
    { data: BodyType<SiteCoverUploadRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return requestSiteCoverUpload(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RequestSiteCoverUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof requestSiteCoverUpload>>
>;
export type RequestSiteCoverUploadMutationBody =
  BodyType<SiteCoverUploadRequest>;
export type RequestSiteCoverUploadMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary Request a direct-upload URL for a site cover image
 */
export const useRequestSiteCoverUpload = <
  TError = ErrorType<
    BadRequestResponse | UnauthorizedResponse | ForbiddenResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestSiteCoverUpload>>,
    TError,
    { data: BodyType<SiteCoverUploadRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof requestSiteCoverUpload>>,
  TError,
  { data: BodyType<SiteCoverUploadRequest> },
  TContext
> => {
  return useMutation(getRequestSiteCoverUploadMutationOptions(options));
};

/**
 * @summary List active sites for staff
 */
export const getListPpmSitesUrl = () => {
  return `/api/ppm/sites`;
};

export const listPpmSites = async (options?: RequestInit): Promise<Site[]> => {
  return customFetch<Site[]>(getListPpmSitesUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListPpmSitesQueryKey = () => {
  return [`/api/ppm/sites`] as const;
};

export const getListPpmSitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listPpmSites>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listPpmSites>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPpmSitesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPpmSites>>> = ({
    signal,
  }) => listPpmSites({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPpmSites>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListPpmSitesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPpmSites>>
>;
export type ListPpmSitesQueryError = ErrorType<UnauthorizedResponse>;

/**
 * @summary List active sites for staff
 */

export function useListPpmSites<
  TData = Awaited<ReturnType<typeof listPpmSites>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listPpmSites>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListPpmSitesQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Serve a validated site cover image to staff
 */
export const getGetSiteCoverUrl = (objectId: string) => {
  return `/api/site-covers/${objectId}`;
};

export const getSiteCover = async (
  objectId: string,
  options?: RequestInit,
): Promise<Blob> => {
  return customFetch<Blob>(getGetSiteCoverUrl(objectId), {
    ...options,
    method: "GET",
  });
};

export const getGetSiteCoverQueryKey = (objectId: string) => {
  return [`/api/site-covers/${objectId}`] as const;
};

export const getGetSiteCoverQueryOptions = <
  TData = Awaited<ReturnType<typeof getSiteCover>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  objectId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSiteCover>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSiteCoverQueryKey(objectId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSiteCover>>> = ({
    signal,
  }) => getSiteCover(objectId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!objectId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSiteCover>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetSiteCoverQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSiteCover>>
>;
export type GetSiteCoverQueryError = ErrorType<
  UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Serve a validated site cover image to staff
 */

export function useGetSiteCover<
  TData = Awaited<ReturnType<typeof getSiteCover>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  objectId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getSiteCover>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetSiteCoverQueryOptions(objectId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns all engineer jobs for administrators, including their immutable site snapshots and report data.
 * @summary List assigned engineer callouts and site surveys
 */
export const getListAdminEngineerJobsUrl = () => {
  return `/api/admin/engineer-jobs`;
};

export const listAdminEngineerJobs = async (
  options?: RequestInit,
): Promise<Job[]> => {
  return customFetch<Job[]>(getListAdminEngineerJobsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListAdminEngineerJobsQueryKey = () => {
  return [`/api/admin/engineer-jobs`] as const;
};

export const getListAdminEngineerJobsQueryOptions = <
  TData = Awaited<ReturnType<typeof listAdminEngineerJobs>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAdminEngineerJobs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAdminEngineerJobsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listAdminEngineerJobs>>
  > = ({ signal }) => listAdminEngineerJobs({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAdminEngineerJobs>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListAdminEngineerJobsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAdminEngineerJobs>>
>;
export type ListAdminEngineerJobsQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse
>;

/**
 * @summary List assigned engineer callouts and site surveys
 */

export function useListAdminEngineerJobs<
  TData = Awaited<ReturnType<typeof listAdminEngineerJobs>>,
  TError = ErrorType<UnauthorizedResponse | ForbiddenResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listAdminEngineerJobs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListAdminEngineerJobsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a job using an active engineer and active site ID, and captures the site details as an immutable snapshot.
 * @summary Assign a callout or site survey to an engineer
 */
export const getCreateAdminEngineerJobUrl = () => {
  return `/api/admin/engineer-jobs`;
};

export const createAdminEngineerJob = async (
  createJobRequest: CreateJobRequest,
  options?: RequestInit,
): Promise<Job> => {
  return customFetch<Job>(getCreateAdminEngineerJobUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createJobRequest),
  });
};

export const getCreateAdminEngineerJobMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAdminEngineerJob>>,
    TError,
    { data: BodyType<CreateJobRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAdminEngineerJob>>,
  TError,
  { data: BodyType<CreateJobRequest> },
  TContext
> => {
  const mutationKey = ["createAdminEngineerJob"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAdminEngineerJob>>,
    { data: BodyType<CreateJobRequest> }
  > = (props) => {
    const { data } = props ?? {};

    return createAdminEngineerJob(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAdminEngineerJobMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAdminEngineerJob>>
>;
export type CreateAdminEngineerJobMutationBody = BodyType<CreateJobRequest>;
export type CreateAdminEngineerJobMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Assign a callout or site survey to an engineer
 */
export const useCreateAdminEngineerJob = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAdminEngineerJob>>,
    TError,
    { data: BodyType<CreateJobRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createAdminEngineerJob>>,
  TError,
  { data: BodyType<CreateJobRequest> },
  TContext
> => {
  return useMutation(getCreateAdminEngineerJobMutationOptions(options));
};

/**
 * Updates assignment or scheduling fields. Administrators can cancel a job or set it back to in progress, but cannot edit its report or change its site or kind.
 * @summary Update an assigned engineer job
 */
export const getUpdateAdminEngineerJobUrl = (id: number) => {
  return `/api/admin/engineer-jobs/${id}`;
};

export const updateAdminEngineerJob = async (
  id: number,
  updateAdminJobRequest: UpdateAdminJobRequest,
  options?: RequestInit,
): Promise<Job> => {
  return customFetch<Job>(getUpdateAdminEngineerJobUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateAdminJobRequest),
  });
};

export const getUpdateAdminEngineerJobMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAdminEngineerJob>>,
    TError,
    { id: number; data: BodyType<UpdateAdminJobRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateAdminEngineerJob>>,
  TError,
  { id: number; data: BodyType<UpdateAdminJobRequest> },
  TContext
> => {
  const mutationKey = ["updateAdminEngineerJob"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateAdminEngineerJob>>,
    { id: number; data: BodyType<UpdateAdminJobRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateAdminEngineerJob(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAdminEngineerJobMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateAdminEngineerJob>>
>;
export type UpdateAdminEngineerJobMutationBody =
  BodyType<UpdateAdminJobRequest>;
export type UpdateAdminEngineerJobMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Update an assigned engineer job
 */
export const useUpdateAdminEngineerJob = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateAdminEngineerJob>>,
    TError,
    { id: number; data: BodyType<UpdateAdminJobRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateAdminEngineerJob>>,
  TError,
  { id: number; data: BodyType<UpdateAdminJobRequest> },
  TContext
> => {
  return useMutation(getUpdateAdminEngineerJobMutationOptions(options));
};

/**
 * Engineers receive only jobs assigned to their account; administrators receive all engineer jobs.
 * @summary List engineer jobs
 */
export const getListEngineerJobsUrl = () => {
  return `/api/engineer/jobs`;
};

export const listEngineerJobs = async (
  options?: RequestInit,
): Promise<Job[]> => {
  return customFetch<Job[]>(getListEngineerJobsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListEngineerJobsQueryKey = () => {
  return [`/api/engineer/jobs`] as const;
};

export const getListEngineerJobsQueryOptions = <
  TData = Awaited<ReturnType<typeof listEngineerJobs>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listEngineerJobs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListEngineerJobsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listEngineerJobs>>
  > = ({ signal }) => listEngineerJobs({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listEngineerJobs>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListEngineerJobsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listEngineerJobs>>
>;
export type ListEngineerJobsQueryError = ErrorType<UnauthorizedResponse>;

/**
 * @summary List engineer jobs
 */

export function useListEngineerJobs<
  TData = Awaited<ReturnType<typeof listEngineerJobs>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listEngineerJobs>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListEngineerJobsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Engineers can read only jobs assigned to their account; administrators may read any engineer job.
 * @summary Get an assigned engineer job
 */
export const getGetEngineerJobUrl = (id: number) => {
  return `/api/engineer/jobs/${id}`;
};

export const getEngineerJob = async (
  id: number,
  options?: RequestInit,
): Promise<Job> => {
  return customFetch<Job>(getGetEngineerJobUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetEngineerJobQueryKey = (id: number) => {
  return [`/api/engineer/jobs/${id}`] as const;
};

export const getGetEngineerJobQueryOptions = <
  TData = Awaited<ReturnType<typeof getEngineerJob>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEngineerJob>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEngineerJobQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEngineerJob>>> = ({
    signal,
  }) => getEngineerJob(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getEngineerJob>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetEngineerJobQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEngineerJob>>
>;
export type GetEngineerJobQueryError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Get an assigned engineer job
 */

export function useGetEngineerJob<
  TData = Awaited<ReturnType<typeof getEngineerJob>>,
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getEngineerJob>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetEngineerJobQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * The assigned engineer may save a draft with partial report strings or submit a completed report. Administrators may read jobs but cannot author an engineer report. Submitted and cancelled jobs are read-only. Submission validates the common visit date, findings, recommendations, and engineer signature; callouts additionally require work completed and system status, while site surveys additionally require survey requirements and proposed solution. Client signature is optional.
 * @summary Save or submit an engineer job report
 */
export const getUpdateEngineerJobReportUrl = (id: number) => {
  return `/api/engineer/jobs/${id}/report`;
};

export const updateEngineerJobReport = async (
  id: number,
  updateJobReportRequest: UpdateJobReportRequest,
  options?: RequestInit,
): Promise<Job> => {
  return customFetch<Job>(getUpdateEngineerJobReportUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(updateJobReportRequest),
  });
};

export const getUpdateEngineerJobReportMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateEngineerJobReport>>,
    TError,
    { id: number; data: BodyType<UpdateJobReportRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateEngineerJobReport>>,
  TError,
  { id: number; data: BodyType<UpdateJobReportRequest> },
  TContext
> => {
  const mutationKey = ["updateEngineerJobReport"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateEngineerJobReport>>,
    { id: number; data: BodyType<UpdateJobReportRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateEngineerJobReport(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateEngineerJobReportMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateEngineerJobReport>>
>;
export type UpdateEngineerJobReportMutationBody =
  BodyType<UpdateJobReportRequest>;
export type UpdateEngineerJobReportMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
>;

/**
 * @summary Save or submit an engineer job report
 */
export const useUpdateEngineerJobReport = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateEngineerJobReport>>,
    TError,
    { id: number; data: BodyType<UpdateJobReportRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateEngineerJobReport>>,
  TError,
  { id: number; data: BodyType<UpdateJobReportRequest> },
  TContext
> => {
  return useMutation(getUpdateEngineerJobReportMutationOptions(options));
};

/**
 * @summary List refrigeration PPM inspections
 */
export const getListPpmInspectionsUrl = () => {
  return `/api/ppm/inspections`;
};

export const listPpmInspections = async (
  options?: RequestInit,
): Promise<PpmInspection[]> => {
  return customFetch<PpmInspection[]>(getListPpmInspectionsUrl(), {
    ...options,
    method: "GET",
  });
};

export const getListPpmInspectionsQueryKey = () => {
  return [`/api/ppm/inspections`] as const;
};

export const getListPpmInspectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listPpmInspections>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listPpmInspections>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPpmInspectionsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listPpmInspections>>
  > = ({ signal }) => listPpmInspections({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPpmInspections>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListPpmInspectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPpmInspections>>
>;
export type ListPpmInspectionsQueryError = ErrorType<UnauthorizedResponse>;

/**
 * @summary List refrigeration PPM inspections
 */

export function useListPpmInspections<
  TData = Awaited<ReturnType<typeof listPpmInspections>>,
  TError = ErrorType<UnauthorizedResponse>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof listPpmInspections>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListPpmInspectionsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Create a refrigeration PPM inspection draft or completed record
 */
export const getCreatePpmInspectionUrl = () => {
  return `/api/ppm/inspections`;
};

export const createPpmInspection = async (
  ppmInspectionInput: PpmInspectionInput,
  options?: RequestInit,
): Promise<PpmInspection> => {
  return customFetch<PpmInspection>(getCreatePpmInspectionUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(ppmInspectionInput),
  });
};

export const getCreatePpmInspectionMutationOptions = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPpmInspection>>,
    TError,
    { data: BodyType<PpmInspectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPpmInspection>>,
  TError,
  { data: BodyType<PpmInspectionInput> },
  TContext
> => {
  const mutationKey = ["createPpmInspection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPpmInspection>>,
    { data: BodyType<PpmInspectionInput> }
  > = (props) => {
    const { data } = props ?? {};

    return createPpmInspection(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePpmInspectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPpmInspection>>
>;
export type CreatePpmInspectionMutationBody = BodyType<PpmInspectionInput>;
export type CreatePpmInspectionMutationError = ErrorType<
  BadRequestResponse | UnauthorizedResponse
>;

/**
 * @summary Create a refrigeration PPM inspection draft or completed record
 */
export const useCreatePpmInspection = <
  TError = ErrorType<BadRequestResponse | UnauthorizedResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPpmInspection>>,
    TError,
    { data: BodyType<PpmInspectionInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createPpmInspection>>,
  TError,
  { data: BodyType<PpmInspectionInput> },
  TContext
> => {
  return useMutation(getCreatePpmInspectionMutationOptions(options));
};

/**
 * @summary Get a refrigeration PPM inspection
 */
export const getGetPpmInspectionUrl = (id: number) => {
  return `/api/ppm/inspections/${id}`;
};

export const getPpmInspection = async (
  id: number,
  options?: RequestInit,
): Promise<PpmInspection> => {
  return customFetch<PpmInspection>(getGetPpmInspectionUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getGetPpmInspectionQueryKey = (id: number) => {
  return [`/api/ppm/inspections/${id}`] as const;
};

export const getGetPpmInspectionQueryOptions = <
  TData = Awaited<ReturnType<typeof getPpmInspection>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPpmInspection>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPpmInspectionQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPpmInspection>>
  > = ({ signal }) => getPpmInspection(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getPpmInspection>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPpmInspectionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPpmInspection>>
>;
export type GetPpmInspectionQueryError = ErrorType<
  UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Get a refrigeration PPM inspection
 */

export function useGetPpmInspection<
  TData = Awaited<ReturnType<typeof getPpmInspection>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPpmInspection>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPpmInspectionQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Update a refrigeration PPM inspection
 */
export const getUpdatePpmInspectionUrl = (id: number) => {
  return `/api/ppm/inspections/${id}`;
};

export const updatePpmInspection = async (
  id: number,
  ppmInspectionPatchInput: PpmInspectionPatchInput,
  options?: RequestInit,
): Promise<PpmInspection> => {
  return customFetch<PpmInspection>(getUpdatePpmInspectionUrl(id), {
    ...options,
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(ppmInspectionPatchInput),
  });
};

export const getUpdatePpmInspectionMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePpmInspection>>,
    TError,
    { id: number; data: BodyType<PpmInspectionPatchInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePpmInspection>>,
  TError,
  { id: number; data: BodyType<PpmInspectionPatchInput> },
  TContext
> => {
  const mutationKey = ["updatePpmInspection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePpmInspection>>,
    { id: number; data: BodyType<PpmInspectionPatchInput> }
  > = (props) => {
    const { id, data } = props ?? {};

    return updatePpmInspection(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdatePpmInspectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof updatePpmInspection>>
>;
export type UpdatePpmInspectionMutationBody = BodyType<PpmInspectionPatchInput>;
export type UpdatePpmInspectionMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Update a refrigeration PPM inspection
 */
export const useUpdatePpmInspection = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePpmInspection>>,
    TError,
    { id: number; data: BodyType<PpmInspectionPatchInput> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updatePpmInspection>>,
  TError,
  { id: number; data: BodyType<PpmInspectionPatchInput> },
  TContext
> => {
  return useMutation(getUpdatePpmInspectionMutationOptions(options));
};

/**
 * @summary Delete a refrigeration PPM inspection
 */
export const getDeletePpmInspectionUrl = (id: number) => {
  return `/api/ppm/inspections/${id}`;
};

export const deletePpmInspection = async (
  id: number,
  options?: RequestInit,
): Promise<SuccessResponse> => {
  return customFetch<SuccessResponse>(getDeletePpmInspectionUrl(id), {
    ...options,
    method: "DELETE",
  });
};

export const getDeletePpmInspectionMutationOptions = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePpmInspection>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePpmInspection>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["deletePpmInspection"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePpmInspection>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return deletePpmInspection(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePpmInspectionMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePpmInspection>>
>;

export type DeletePpmInspectionMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Delete a refrigeration PPM inspection
 */
export const useDeletePpmInspection = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePpmInspection>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deletePpmInspection>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation(getDeletePpmInspectionMutationOptions(options));
};

/**
 * Returns a provisional direct-upload URL. The photo is not active until the upload is confirmed. Only an administrator or the inspection's assigned engineer may request an upload; an assigned engineer may do so only while the inspection is a draft. An unrelated authenticated user receives 404. Photos are optional and never required to complete an inspection.
 * @summary Request a PPM inspection checklist photo upload
 */
export const getRequestPpmInspectionPhotoUploadUrl = (id: number) => {
  return `/api/ppm/inspections/${id}/photos/upload`;
};

export const requestPpmInspectionPhotoUpload = async (
  id: number,
  ppmChecklistPhotoUploadRequest: PpmChecklistPhotoUploadRequest,
  options?: RequestInit,
): Promise<PpmChecklistPhotoUploadResponse> => {
  return customFetch<PpmChecklistPhotoUploadResponse>(
    getRequestPpmInspectionPhotoUploadUrl(id),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(ppmChecklistPhotoUploadRequest),
    },
  );
};

export const getRequestPpmInspectionPhotoUploadMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>,
    TError,
    { id: number; data: BodyType<PpmChecklistPhotoUploadRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>,
  TError,
  { id: number; data: BodyType<PpmChecklistPhotoUploadRequest> },
  TContext
> => {
  const mutationKey = ["requestPpmInspectionPhotoUpload"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>,
    { id: number; data: BodyType<PpmChecklistPhotoUploadRequest> }
  > = (props) => {
    const { id, data } = props ?? {};

    return requestPpmInspectionPhotoUpload(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RequestPpmInspectionPhotoUploadMutationResult = NonNullable<
  Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>
>;
export type RequestPpmInspectionPhotoUploadMutationBody =
  BodyType<PpmChecklistPhotoUploadRequest>;
export type RequestPpmInspectionPhotoUploadMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Request a PPM inspection checklist photo upload
 */
export const useRequestPpmInspectionPhotoUpload = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>,
    TError,
    { id: number; data: BodyType<PpmChecklistPhotoUploadRequest> },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof requestPpmInspectionPhotoUpload>>,
  TError,
  { id: number; data: BodyType<PpmChecklistPhotoUploadRequest> },
  TContext
> => {
  return useMutation(
    getRequestPpmInspectionPhotoUploadMutationOptions(options),
  );
};

/**
 * Validates the uploaded object's bytes, media type, and size before activation. After successful validation, atomically replaces the existing active photo for the same task and stage. Failed validation leaves the existing photo active. Only an administrator or the inspection's assigned engineer may confirm; an assigned engineer may do so only while the inspection is a draft. An unrelated authenticated user receives 404.
 * @summary Confirm a PPM inspection checklist photo upload
 */
export const getConfirmPpmInspectionPhotoUrl = (
  id: number,
  photoId: string,
) => {
  return `/api/ppm/inspections/${id}/photos/${photoId}/confirm`;
};

export const confirmPpmInspectionPhoto = async (
  id: number,
  photoId: string,
  options?: RequestInit,
): Promise<PpmChecklistPhoto> => {
  return customFetch<PpmChecklistPhoto>(
    getConfirmPpmInspectionPhotoUrl(id, photoId),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getConfirmPpmInspectionPhotoMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>,
    TError,
    { id: number; photoId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>,
  TError,
  { id: number; photoId: string },
  TContext
> => {
  const mutationKey = ["confirmPpmInspectionPhoto"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>,
    { id: number; photoId: string }
  > = (props) => {
    const { id, photoId } = props ?? {};

    return confirmPpmInspectionPhoto(id, photoId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ConfirmPpmInspectionPhotoMutationResult = NonNullable<
  Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>
>;

export type ConfirmPpmInspectionPhotoMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
>;

/**
 * @summary Confirm a PPM inspection checklist photo upload
 */
export const useConfirmPpmInspectionPhoto = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>,
    TError,
    { id: number; photoId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof confirmPpmInspectionPhoto>>,
  TError,
  { id: number; photoId: string },
  TContext
> => {
  return useMutation(getConfirmPpmInspectionPhotoMutationOptions(options));
};

/**
 * Returns the active before and after photo for each task/stage pair. Administrators and the inspection's assigned engineer may list photos for inspections in any status. An unrelated authenticated user receives 404. Photos are stored separately from the PPM inspection JSON form and are not affected by saving a form draft.
 * @summary List PPM inspection checklist photos
 */
export const getListPpmInspectionPhotosUrl = (id: number) => {
  return `/api/ppm/inspections/${id}/photos`;
};

export const listPpmInspectionPhotos = async (
  id: number,
  options?: RequestInit,
): Promise<PpmChecklistPhoto[]> => {
  return customFetch<PpmChecklistPhoto[]>(getListPpmInspectionPhotosUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getListPpmInspectionPhotosQueryKey = (id: number) => {
  return [`/api/ppm/inspections/${id}/photos`] as const;
};

export const getListPpmInspectionPhotosQueryOptions = <
  TData = Awaited<ReturnType<typeof listPpmInspectionPhotos>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listPpmInspectionPhotos>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListPpmInspectionPhotosQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listPpmInspectionPhotos>>
  > = ({ signal }) =>
    listPpmInspectionPhotos(id, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listPpmInspectionPhotos>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListPpmInspectionPhotosQueryResult = NonNullable<
  Awaited<ReturnType<typeof listPpmInspectionPhotos>>
>;
export type ListPpmInspectionPhotosQueryError = ErrorType<
  UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary List PPM inspection checklist photos
 */

export function useListPpmInspectionPhotos<
  TData = Awaited<ReturnType<typeof listPpmInspectionPhotos>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listPpmInspectionPhotos>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListPpmInspectionPhotosQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes the active checklist photo. Only an administrator or the inspection's assigned engineer may delete; an assigned engineer may do so only while the inspection is a draft. An unrelated authenticated user receives 404.
 * @summary Delete a PPM inspection checklist photo
 */
export const getDeletePpmInspectionPhotoUrl = (id: number, photoId: string) => {
  return `/api/ppm/inspections/${id}/photos/${photoId}`;
};

export const deletePpmInspectionPhoto = async (
  id: number,
  photoId: string,
  options?: RequestInit,
): Promise<void> => {
  return customFetch<void>(getDeletePpmInspectionPhotoUrl(id, photoId), {
    ...options,
    method: "DELETE",
  });
};

export const getDeletePpmInspectionPhotoMutationOptions = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePpmInspectionPhoto>>,
    TError,
    { id: number; photoId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePpmInspectionPhoto>>,
  TError,
  { id: number; photoId: string },
  TContext
> => {
  const mutationKey = ["deletePpmInspectionPhoto"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePpmInspectionPhoto>>,
    { id: number; photoId: string }
  > = (props) => {
    const { id, photoId } = props ?? {};

    return deletePpmInspectionPhoto(id, photoId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePpmInspectionPhotoMutationResult = NonNullable<
  Awaited<ReturnType<typeof deletePpmInspectionPhoto>>
>;

export type DeletePpmInspectionPhotoMutationError = ErrorType<
  UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
>;

/**
 * @summary Delete a PPM inspection checklist photo
 */
export const useDeletePpmInspectionPhoto = <
  TError = ErrorType<
    UnauthorizedResponse | ForbiddenResponse | NotFoundResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePpmInspectionPhoto>>,
    TError,
    { id: number; photoId: string },
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deletePpmInspectionPhoto>>,
  TError,
  { id: number; photoId: string },
  TContext
> => {
  return useMutation(getDeletePpmInspectionPhotoMutationOptions(options));
};

/**
 * Serves the validated photo bytes. Administrators and the inspection's assigned engineer may access images for inspections in any status. An unrelated authenticated user receives 404.
 * @summary Get a PPM inspection checklist photo image
 */
export const getGetPpmInspectionPhotoImageUrl = (
  id: number,
  photoId: string,
) => {
  return `/api/ppm/inspections/${id}/photos/${photoId}/image`;
};

export const getPpmInspectionPhotoImage = async (
  id: number,
  photoId: string,
  options?: RequestInit,
): Promise<Blob> => {
  return customFetch<Blob>(getGetPpmInspectionPhotoImageUrl(id, photoId), {
    ...options,
    method: "GET",
  });
};

export const getGetPpmInspectionPhotoImageQueryKey = (
  id: number,
  photoId: string,
) => {
  return [`/api/ppm/inspections/${id}/photos/${photoId}/image`] as const;
};

export const getGetPpmInspectionPhotoImageQueryOptions = <
  TData = Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  photoId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPpmInspectionPhotoImageQueryKey(id, photoId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>
  > = ({ signal }) =>
    getPpmInspectionPhotoImage(id, photoId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!(id && photoId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetPpmInspectionPhotoImageQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>
>;
export type GetPpmInspectionPhotoImageQueryError = ErrorType<
  UnauthorizedResponse | NotFoundResponse
>;

/**
 * @summary Get a PPM inspection checklist photo image
 */

export function useGetPpmInspectionPhotoImage<
  TData = Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>,
  TError = ErrorType<UnauthorizedResponse | NotFoundResponse>,
>(
  id: number,
  photoId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPpmInspectionPhotoImage>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetPpmInspectionPhotoImageQueryOptions(
    id,
    photoId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  return { ...query, queryKey: queryOptions.queryKey };
}
